import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import { featuredComics, dailyUpdateComics } from '@/mocks/comics';
import type { ComicArticle } from '@/mocks/comics';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  const allComics: ComicArticle[] = [...featuredComics, ...dailyUpdateComics];
  const comic = allComics.find((c) => c.slug === slug);

  const relatedComics = allComics
    .filter((c) => c.slug !== slug && c.categorySlug === comic?.categorySlug)
    .slice(0, 3);

  if (!comic) {
    return (
      <>
        <SEO title="作品未找到 - 韩国漫画网" description="该漫画作品不存在或已下架" />
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-3">作品未找到</h1>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleTitle = `${comic.title} - ${comic.author} - 韩国免费漫画`;
  const articleDesc = `${comic.description} 作者${comic.author}，${comic.status}，已更新${comic.totalChapters}话。在韩国漫画网免费阅读下拉式全集。`;
  const articleKeywords = `韩国免费漫画,${comic.title},${comic.author},${comic.category},韩漫大全,下拉式漫画`;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://bendionline.com/' },
      { '@type': 'ListItem', position: 2, name: comic.category, item: 'http://bendionline.com/' },
      { '@type': 'ListItem', position: 3, name: comic.title, item: `http://bendionline.com/article/${comic.slug}` },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: comic.title,
    description: comic.description,
    image: comic.coverImage,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    dateModified: comic.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '韩国漫画网',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${comic.title}更新频率是多少？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${comic.title}每周定期更新，最新话已更新至第${comic.totalChapters}话，韩国漫画网与韩方同步更新，确保读者第一时间看到最新内容。`,
        },
      },
      {
        '@type': 'Question',
        name: `${comic.title}可以在手机上看吗？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `可以。韩国漫画网支持手机端下拉式阅读，在任何设备上都能流畅阅读${comic.title}的全部章节。`,
        },
      },
      {
        '@type': 'Question',
        name: `${comic.title}是免费阅读吗？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `是的，${comic.title}在韩国漫画网提供免费在线阅读，无需付费即可享受高清下拉式阅读体验。`,
        },
      },
    ],
  };

  return (
    <>
      <SEO
        title={articleTitle}
        description={articleDesc}
        keywords={articleKeywords}
        ogType="article"
        ogImage={comic.coverImage}
        canonicalPath={`/article/${comic.slug}`}
        schema={{ ...breadcrumbSchema, ...articleSchema }}
      />
      <Navbar />

      <main>
        <section className="relative w-full h-[400px] md:h-[520px] overflow-hidden">
          <img
            src={comic.coverImage}
            alt={comic.title}
            title={`${comic.title} - 韩国免费漫画`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/65"></div>

          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-4">
            <nav className="mb-4 text-xs md:text-sm text-white/70" aria-label="面包屑导航">
              <Link to="/" className="hover:text-white transition-colors whitespace-nowrap cursor-pointer">首页</Link>
              <span className="mx-2">/</span>
              <Link to="/" className="hover:text-white transition-colors whitespace-nowrap cursor-pointer">{comic.category}</Link>
              <span className="mx-2">/</span>
              <span className="text-white">{comic.title}</span>
            </nav>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-3 md:mb-4">
              {comic.title}
            </h1>
            <p className="text-sm md:text-base text-white/80 max-w-lg mb-6">
              {comic.author} · {comic.status} · {comic.totalChapters}话
            </p>

            <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
              {comic.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 text-xs rounded-full bg-white/15 text-white border border-white/20">
                  {tag}
                </span>
              ))}
            </div>

            <button
              type="button"
              className="px-10 py-3.5 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              立即阅读 第{comic.totalChapters}话
            </button>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-[1200px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2">
                <div className="mb-8">
                  <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-accent-100 text-accent-800 mb-3">
                    作品简介
                  </span>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    {comic.longDescription}
                  </p>
                </div>

                <div className="mb-8">
                  <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                    章节列表
                  </h2>
                  <div className="flex flex-col gap-2">
                    {comic.chapters.map((ch) => (
                      <div
                        key={ch.id}
                        className="flex items-center justify-between px-4 py-3 bg-background-50 rounded-lg cursor-pointer hover:bg-background-200/70 transition-colors"
                      >
                        <span className="text-sm text-foreground-800">{ch.title}</span>
                        <span className="text-xs text-foreground-500">{ch.updatedAt}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {comic.reviews.length > 0 && (
                  <div>
                    <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                      读者评论 ({comic.reviews.length})
                    </h2>
                    <div className="flex flex-col gap-4">
                      {comic.reviews.map((review) => (
                        <div key={review.id} className="bg-background-50 rounded-xl p-4 md:p-5">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                              <img src={review.avatar} alt={review.author} className="w-full h-full object-cover" />
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-semibold text-foreground-950">{review.author}</p>
                              <div className="flex items-center gap-2">
                                <div className="flex gap-0.5">
                                  {Array.from({ length: review.rating }).map((_, i) => (
                                    <span key={i} className="w-3 h-3 flex items-center justify-center">
                                      <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                                    </span>
                                  ))}
                                </div>
                                <span className="text-xs text-foreground-500">{review.date}</span>
                              </div>
                            </div>
                          </div>
                          <p className="text-sm text-foreground-700 leading-relaxed">{review.content}</p>
                          <div className="mt-3 flex items-center gap-4">
                            <button type="button" className="flex items-center gap-1 text-xs text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer">
                              <span className="w-3.5 h-3.5 flex items-center justify-center">
                                <i className="ri-thumb-up-line"></i>
                              </span>
                              {review.likes}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="lg:col-span-1">
                <div className="bg-background-50 rounded-xl p-5 md:p-6 sticky top-24">
                  <h3 className="text-base font-heading font-bold text-foreground-950 mb-4">
                    作品情报
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-2xl font-bold text-foreground-950">{comic.totalViews}</p>
                      <p className="text-xs text-foreground-500 mt-1">总阅读量</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground-950">{comic.totalChapters}</p>
                      <p className="text-xs text-foreground-500 mt-1">总章节数</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground-950">{comic.ratingPercent}</p>
                      <p className="text-xs text-foreground-500 mt-1">好评率</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground-950">{comic.status}</p>
                      <p className="text-xs text-foreground-500 mt-1">连载状态</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
            <div className="max-w-[1200px] mx-auto">
              <div className="mb-10">
                <h2 className="text-2xl font-heading font-bold text-foreground-950">
                  同类推荐
                </h2>
                <p className="text-sm text-foreground-600 mt-1">
                  更多{comic.category}题材漫画
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
                {relatedComics.map((rc) => (
                  <Link
                    key={rc.slug}
                    to="/"
                    className="group bg-background-100 rounded-xl overflow-hidden cursor-pointer transition-all hover:-translate-y-1"
                  >
                    <div className="relative w-full h-[280px] md:h-[320px]">
                      <img src={rc.coverImage} alt={rc.title} title={rc.title} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="p-4 text-center">
                      <h3 className="text-sm font-semibold text-foreground-950 mb-1">{rc.title}</h3>
                      <p className="text-xs text-foreground-500 mb-3">{rc.author} · {rc.totalChapters}话</p>
                      <span className="inline-block w-full py-2.5 text-xs font-semibold rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap">
                        开始阅读
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="relative w-full py-16 md:py-24 px-4 md:px-6 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20coral%20gradient%20abstract%20background%2C%20soft%20flowing%20shapes%2C%20dreamy%20reading%20atmosphere%2C%20clean%20minimal%20composition%2C%20korean%20webtoon%20platform%20branding&width=1600&height=700&seq=cta-article-2026&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/55"></div>
          <div className="relative z-10 text-center max-w-[700px] mx-auto">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-white mb-4">
              继续阅读下一话
            </h2>
            <p className="text-sm md:text-base text-white/80 mb-8">
              下拉式漫画阅读体验，流畅顺滑，沉浸式感受韩漫魅力
            </p>
            <button
              type="button"
              className="inline-flex items-center gap-2 px-10 py-3.5 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              立即阅读
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </span>
            </button>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    </>
  );
}